import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Loading from "../components/Loading";
import {
  getPermittedRoutes,
  RouteConfig,
  routesConfig,
} from "../config/routes";
import { httpService } from "../services/httpService";
import { useAuth } from "../hooks";
import { IPermissionCategories } from "../features/Configurations/TeamManagement/interface";
import { PERMISSION_MAPPER } from "../shared/constants";
import toast from "react-hot-toast";

// 🔍 Improved matcher (prefix + dynamic support) — pure function, no component state
const matchRoute = (pathname: string, routePath: string): boolean => {
  const pathSegments = pathname.split("/").filter(Boolean);
  const routeSegments = routePath.split("/").filter(Boolean);

  if (routeSegments.length === 0) return pathSegments.length === 0;
  if (routeSegments.length > pathSegments.length) return false;

  return routeSegments.every((segment, index) => {
    if (segment.startsWith(":")) return true;
    return segment === pathSegments[index];
  });
};

// 🔥 Correct resolution: submenu FIRST — pure function, no component state
const resolveRoute = (pathname: string) => {
  let currentRoute: RouteConfig | undefined;
  let submenuRoute: RouteConfig | undefined;

  for (const route of routesConfig) {
    if (route.submenu) {
      const foundSub = route.submenu.find((sub) =>
        matchRoute(pathname, sub.path)
      );
      if (foundSub) {
        return { currentRoute: route, submenuRoute: foundSub };
      }
    }
    if (matchRoute(pathname, route.path)) {
      currentRoute = route;
    }
  }

  return { currentRoute, submenuRoute };
};

const RouteInspector: React.FC = () => {
  const { user, isLoading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasCheckedRoute, setHasCheckedRoute] = useState(false);

  useEffect(() => {
    if (isLoading) return;

    const accessToken = httpService.getAccessToken();
    const { currentRoute, submenuRoute } = resolveRoute(location.pathname);

    // ✅ Public route
    if (!currentRoute?.protected) {

      setHasCheckedRoute(true);
      return;
    }

    // 🔐 No token
    if (!accessToken) {

      if (location.pathname !== "/auth") {
        navigate("/auth");
      }
      setHasCheckedRoute(true);
      return;
    }

    // ❌ Invalid user
    if (!user) {

      httpService.clearAllData();
      navigate("/auth");
      setHasCheckedRoute(true);
      return;
    }

    // 🛠️ Workspace setup in progress — lock user to /workspace-setup
    const workspaceSetupPending = localStorage.getItem('workspace_setup_pending');
    if (workspaceSetupPending && location.pathname !== '/workspace-setup') {
      navigate('/workspace-setup');
      setHasCheckedRoute(true);
      return;
    }

    // 👑 VIP users have no billing/pricing — redirect to dashboard
    if (
      user.is_vip &&
      ['/pricing', '/billing', '/billing-history', '/plans-and-billing'].includes(location.pathname)
    ) {
      navigate('/dashboard');
      setHasCheckedRoute(true);
      return;
    }


    // 👑 Full access bypass — superuser, "*" or VIP schema-level access
    if (
      user.is_superuser ||
      user.user_permissions === "*" ||
      user.user_permissions === PERMISSION_MAPPER.SCHEMA_LEVEL_ACCESS
    ) {
      setHasCheckedRoute(true);
      return;
    }

    // 👥 Team member gate — users who belong to an organisation (have a parent)
    const isTeamMember = !user.is_team_admin && !!user.parent;

    if (isTeamMember) {
      const parent = user.parent!;
      const parentHasActiveSubscription =
        parent.subscription_status === 'active' ||
        parent.subscription_status === 'trialing';

      // Parent has neither VIP nor an active plan → org has no access, force logout
      if (!parent.is_vip && !parentHasActiveSubscription && !parent.plan) {
        toast.error(
          "Your organisation doesn't have an active plan. Please contact your team admin to restore access."
        );
        logout();
        setHasCheckedRoute(true);
        return;
      }

      // Team members never reach the pricing page — redirect to dashboard
      if (location.pathname === '/pricing') {
        const permittedRoutes = getPermittedRoutes(user);
        navigate(permittedRoutes[0]?.path || '/dashboard');
        setHasCheckedRoute(true);
        return;
      }

      // Parent has an active plan/VIP — skip the subscription gate below,
      // fall through to whitelist + permission checks
    }

    // ✅ Whitelisted routes
    if (
      [
        "/profile",
        "/user-activity",
        "/support",
        "/pricing",
        "/billing",
        "/billing-history",
        "/plans-and-billing",
      ].includes(location.pathname)
    ) {
      setHasCheckedRoute(true);
      return;
    }

    // 💳 Subscription gate — team admins without an active plan can only access /pricing
    if (!isTeamMember) {
      const hasActiveSubscription =
        user.subscription_status === 'active' ||
        user.subscription_status === 'trialing';

      if (!user.is_vip && !hasActiveSubscription) {
        if (location.pathname !== '/pricing') {
          navigate('/pricing');
        }
        setHasCheckedRoute(true);
        return;
      }
    }

    // 🚨 Route not found
    if (!currentRoute && !submenuRoute) {
      navigate("/404");
      setHasCheckedRoute(true);
      return;
    }

    const permittedRoutes = getPermittedRoutes(user);

    // 🔐 Permission resolution (submenu priority)
    const permission = submenuRoute?.permission ?? currentRoute?.permission;

    const isUserRoleAllowed = permission
      ? (user.user_permissions as IPermissionCategories).modules?.some(
        (p) => p.codename === permission
      )
      : true;

    // 🚫 Block access
    if (!isUserRoleAllowed) {
      const firstPath = permittedRoutes?.[0]?.path || "/404";

      // Safety check: only navigate if we are moving to a DIFFERENT path
      if (location.pathname !== firstPath) {
        setHasCheckedRoute(true);
        navigate(firstPath);
        return;
      }
    }

    setHasCheckedRoute(true);
  }, [user, isLoading, location.pathname, navigate, logout]);

  if (isLoading || !hasCheckedRoute) {
    return <Loading />;
  }

  return <Outlet />;
};

export default RouteInspector;